import { ArrowDropDown, ArrowDropUp } from '@mui/icons-material';
import {
  Box,
  Button,
  CircularProgress,
  Link,
  Menu,
  MenuItem,
} from '@mui/material';
import { ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { PersonContract } from '~/data/employeeProfile';
import { useAllEmployees } from '~/data/employees';
import { useMenuButton } from '~/hooks/useMenu';
import {
  OverlappingEmployment,
  useOverlappingEmployments,
} from '~/legacy-components/LegacyEmployeeCard/useOverlappingEmployments';
import {
  EmployeeProfileDto,
  EmployeeShortDto,
  PersonContactDto,
} from '~/services/Http/client.people.generated';

import { useSxStyles } from './useStyles';

export type LegacyEmployeeCardPropsType = {
  profile: EmployeeProfileDto;
  contracts?: PersonContract[];
  employeeId?: number;
  children?: ReactNode;
  overlappingEmploymentHref?: (employeeId: number) => string;
};

export const LegacyEmployeeCard = (props: LegacyEmployeeCardPropsType) => {
  const {
    children,
    profile: {
      id,
      profilePic,
      firstName,
      lastName,
      unit,
      manager,
      contact,
      positionName,
    },
    contracts,
    overlappingEmploymentHref,
  } = props;

  const sxStyles = useSxStyles();
  const { t } = useTranslation();

  const { allEmployees } = useAllEmployees();
  const { isExt } = useMemo(() => {
    const foundEmployee = allEmployees[id];

    return {
      isExt: !foundEmployee?.fEmpHasAccount,
    };
  }, [allEmployees, id]);

  const { overlappingEmployments, isLoading: isLoadingOverlapping } =
    useOverlappingEmployments({
      contracts: !!contracts ? contracts : [],
    });
  const employmentsMenu = useMenuButton();

  const renderManagerLink = (manager: EmployeeShortDto) => {
    const href = `/v2/portal/employee/${manager.id}/people`;
    return (
      <a href={href}>
        <strong>
          {manager.lastName}, {manager.firstName}
        </strong>
      </a>
    );
  };

  const renderPosition = () => {
    if ((!!contracts && contracts.length <= 1) || !overlappingEmploymentHref) {
      return <Box>{positionName.value}</Box>;
    }

    return (
      <Box>
        <Button
          size={'small'}
          onClick={employmentsMenu.open}
          variant="contained"
          sx={sxStyles.dropdownButton}
          endIcon={employmentsMenu.isOpen ? <ArrowDropUp /> : <ArrowDropDown />}
          disableElevation
        >
          {positionName.value}
        </Button>

        <Menu
          anchorEl={employmentsMenu.anchorEl}
          open={employmentsMenu.isOpen}
          onClose={() => {
            employmentsMenu.close();
          }}
          MenuListProps={{
            sx: {
              width:
                employmentsMenu.anchorEl &&
                employmentsMenu.anchorEl?.offsetWidth,
            },
          }}
        >
          {isLoadingOverlapping ? (
            <MenuItem sx={sxStyles.dropdownLoading}>
              <CircularProgress />
            </MenuItem>
          ) : (
            overlappingEmployments
              .filter((c) => c.employeeId !== id)
              .map((contract: OverlappingEmployment) => {
                return (
                  <MenuItem
                    sx={sxStyles.dropdownItem}
                    key={`contract-${contract.employeeId}`}
                    onClick={employmentsMenu.close}
                    component={Link}
                    href={overlappingEmploymentHref(contract.employeeId)}
                  >
                    <Box sx={sxStyles.dropdownLink}>
                      <Box component={'span'} className={'text'}>
                        {contract.unitPosition}
                      </Box>
                      <Box
                        component={'small'}
                        className={'description'}
                      >{`${contract.unitName} (${contract.unitNumber})`}</Box>
                    </Box>
                  </MenuItem>
                );
              })
          )}
        </Menu>
      </Box>
    );
  };

  const renderEmailLink = (email: string | undefined) => {
    return email ? (
      <Box>
        <Link href={`mailto:${email}`}>{email}</Link>
      </Box>
    ) : null;
  };

  const renderPhoneNumbers = (personContact: PersonContactDto | undefined) => {
    if (!personContact) {
      return null;
    }

    const phones: string[] = [personContact.phone, personContact.mobile].filter(
      (v: string | undefined): v is string => v !== undefined,
    );

    return phones.length > 0 ? <div>{phones.join(', ')}</div> : null;
  };

  /**
   * TODO Missing Legacy feature: Notebook button (used by manager roles)
   */

  return (
    <Box sx={sxStyles.root}>
      <Box sx={sxStyles.picture}>
        <img src={profilePic} alt={`${firstName} ${lastName}`} />
      </Box>
      <Box sx={sxStyles.summary}>
        <Box sx={sxStyles.name}>
          {firstName} {lastName}{' '}
          {isExt && <Box sx={sxStyles.ext}>{t('ext')}</Box>}
        </Box>
        {renderPosition()}
        <Box>
          {unit.name} ({unit.code})
          {!!manager && <>, {renderManagerLink(manager)}</>}
        </Box>
        {renderEmailLink(contact.work?.email)}
        {renderPhoneNumbers(contact.work)}
        {!!children && <Box>{children}</Box>}
      </Box>
    </Box>
  );
};
